// Gymkee 2027 coach-colour contrast gate. Pure functions, no dependencies.
// Rule (DS21 The coach's colour): a coach picks one hex. The label on that fill is ink #141110 when
// ink reaches 4.5:1 on it, else paper #FDF9F6. As text on paper the colour is used only when it reaches
// 4.5:1; otherwise a darker step is derived by walking lightness down in half steps, same hue and
// saturation, until it passes. Gymkee orange #FF8D27 keeps ink on the fill (8.14:1) and derives #B85700 as text.
// Same maths for every runtime: coach web, website, athlete (TypeScript), and the ports in Swift and Kotlin
// must reproduce these numbers on the fixtures in `contrast-gate.fixtures.json`.

export const INK = "#141110";
export const PAPER = "#FDF9F6";
export const MIN_TEXT = 4.5;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}
export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const n = parseInt(full.slice(0, 6), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const R = r / 255,
    G = g / 255,
    B = b / 255;
  const max = Math.max(R, G, B),
    min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === R) h = (G - B) / d + (G < B ? 6 : 0);
  else if (max === G) h = (B - R) / d + 2;
  else h = (R - G) / d + 4;
  return { h: h * 60, s, l };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return { r: f(0) * 255, g: f(8) * 255, b: f(4) * 255 };
}

export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a),
    lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

/** The label colour on a fill of the coach's colour: ink when ink passes 4.5:1, else paper. */
export function labelOnFill(fill: string): {
  label: string;
  ratio: number;
  usesInk: boolean;
} {
  const ink = contrastRatio(fill, INK);
  if (ink >= MIN_TEXT) return { label: INK, ratio: ink, usesInk: true };
  return { label: PAPER, ratio: contrastRatio(fill, PAPER), usesInk: false };
}

/** The coach's colour as text on paper: the colour itself when it passes, else the nearest darker step that does. */
export function textOnPaper(
  hex: string,
  ground: string = PAPER,
): { text: string; ratio: number; derived: boolean } {
  const base = contrastRatio(hex, ground);
  if (base >= MIN_TEXT)
    return { text: hex.toUpperCase(), ratio: base, derived: false };
  const hsl = rgbToHsl(hexToRgb(hex));
  for (let step = 1; step <= 40; step += 1) {
    const l = Math.max(0, hsl.l - step * 0.0125);
    const candidate = rgbToHex(hslToRgb({ ...hsl, l }));
    const ratio = contrastRatio(candidate, ground);
    if (ratio >= MIN_TEXT) return { text: candidate, ratio, derived: true };
  }
  return { text: INK, ratio: contrastRatio(INK, ground), derived: true };
}

/** The 50 to 950 ramp from one hex: lighter steps mix toward paper, darker steps keep the hue and drop lightness, floor at 6 percent. */
export function ramp(hex: string): Record<string, string> {
  const hsl = rgbToHsl(hexToRgb(hex));
  const out: Record<string, string> = {};
  const lighter: Array<[string, number]> = [
    ["50", 0.96],
    ["100", 0.91],
    ["200", 0.82],
    ["300", 0.72],
    ["400", 0.62],
  ];
  for (const [k, l] of lighter)
    out[k] = rgbToHex(
      hslToRgb({
        h: hsl.h,
        s: Math.min(1, hsl.s * 0.95),
        l: Math.max(hsl.l, l),
      }),
    );
  out["500"] = hex.toUpperCase();
  const darker: Array<[string, number]> = [
    ["600", 0.86],
    ["700", 0.72],
    ["800", 0.56],
    ["900", 0.4],
    ["950", 0.26],
  ];
  for (const [k, f] of darker)
    out[k] = rgbToHex(
      hslToRgb({ h: hsl.h, s: hsl.s, l: Math.max(0.06, hsl.l * f) }),
    );
  return out;
}

/** Everything a runtime needs for one coach colour, in one call. */
export function coachColour(hex: string): {
  fill: string;
  label: string;
  labelRatio: number;
  text: string;
  textRatio: number;
  textDerived: boolean;
  ramp: Record<string, string>;
} {
  const fill = hex.toUpperCase();
  const lab = labelOnFill(fill);
  const txt = textOnPaper(fill);
  return {
    fill,
    label: lab.label,
    labelRatio: lab.ratio,
    text: txt.text,
    textRatio: txt.ratio,
    textDerived: txt.derived,
    ramp: ramp(fill),
  };
}
